import { useState, useCallback, useRef, useEffect } from "react";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { decodeImage, canvasToBlob, flattenOnColor } from "./decode";
import { getImageOutputFormat, withCorrectExtension, resizeImage, cropImage, canvasToBlobMatchingFormat } from "./operations";
import { fitCanvas, rotateFlip, toGrayscale } from "./convertBrowser";
import { FIT_MODES, parseColor, type FitMode } from "./convertCore";
import { FIT_HELP, turnedSize } from "./convertForm";
import { Check, ColorPicker } from "./ConvertOptionsPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Crop, Maximize2 } from "lucide-react";

const TOOL_INFO_CONTENT = {
  what: `Changes an image's dimensions, either by resizing it to a specific width and height, or by cropping it down to just the area you want to keep. Resizing offers the same five fit modes as the FreeDailyPro API (inside, contain, cover, fill and outside), and can also turn or mirror the picture and make it black and white.`,
  how: `Upload your image, pick Resize or Crop. For Resize, enter your target width and height — with "Maintain aspect ratio" on (the default), changing one dimension automatically adjusts the other so the image never distorts; turn it off if you specifically want independent width and height, then pick how the picture fits the box: cover crops the overflow, contain pads the leftover space, fill stretches, inside and outside keep the shape. A live preview updates as you type. For Crop, click and drag directly on the image to select the area to keep — you'll see the selection take shape as you drag, then apply it. Download the result when you're done.`,
  tip: `With aspect ratio locked, you can't stretch or squash the image — that's the point, but it also means you can't force an exact width AND height unless they already match the original proportions. Turn the lock off if you genuinely need specific, independent dimensions and are fine with some distortion.`,
};

type Mode = "resize" | "crop";
const MIN_CROP_FRAC = 0.02;

// null = freeform (current drag-anywhere behavior, unchanged). A
// numeric value constrains the drag selection to that width/height
// ratio -- the common social-media aspect set competitors already
// offer (1:1 square, 16:9 widescreen, 4:5 portrait, 9:16 story/reel),
// rather than only ever supporting an arbitrary freeform rectangle.
/** Largest output: 16384 px on a side (browser canvas limit) and 100 megapixels in all. */
const MAX_SIDE = 16384;
const MAX_PIXELS = 100_000_000;

/** What is wrong with a typed width or height, or null when it is fine. */
function sizeProblem(label: string, text: string): string | null {
  if (text.trim() === "") return `Enter a ${label}`;
  const n = Number(text);
  if (!Number.isFinite(n)) return `${label[0].toUpperCase()}${label.slice(1)} must be a number`;
  if (!Number.isInteger(n)) return `${label[0].toUpperCase()}${label.slice(1)} must be a whole number of pixels`;
  if (n < 1) return `${label[0].toUpperCase()}${label.slice(1)} must be at least 1 px`;
  if (n > MAX_SIDE) return `${label[0].toUpperCase()}${label.slice(1)} can be at most ${MAX_SIDE.toLocaleString("en-US")} px`;
  return null;
}

const ASPECT_PRESETS: { label: string; ratio: number | null }[] = [
  { label: "Freeform", ratio: null },
  { label: "1:1", ratio: 1 },
  { label: "16:9", ratio: 16 / 9 },
  { label: "4:3", ratio: 4 / 3 },
  { label: "3:2", ratio: 3 / 2 },
  { label: "3:4", ratio: 3 / 4 },
  { label: "2:3", ratio: 2 / 3 },
  { label: "4:5", ratio: 4 / 5 },
  { label: "9:16", ratio: 9 / 16 },
];

export default function ResizeCrop() {
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<Mode>("resize");
  const [unit, setUnit] = useState<"%" | "cm" | "in">("%");
  const [unitValue, setUnitValue] = useState("");
  const [dpi, setDpi] = useState("300");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [maintainRatio, setMaintainRatio] = useState(true);
  // Fit modes only matter once both sides are typed in by hand; "fill" is the stretch this tool always did.
  const [fit, setFit] = useState<FitMode>("fill");
  const [noEnlarge, setNoEnlarge] = useState(false);
  const [padColor, setPadColor] = useState("#ffffff");
  const [usePad, setUsePad] = useState(false);
  const [rotate, setRotate] = useState("0");
  const [flip, setFlip] = useState(false);
  const [flop, setFlop] = useState(false);
  const [grayscale, setGrayscale] = useState(false);
  const [resizePreviewUrl, setResizePreviewUrl] = useState<string | null>(null);
  const [cropResultUrl, setCropResultUrl] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<Blob | null>(null);
  const [origDims, setOrigDims] = useState({ w: 0, h: 0 });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragCurrent, setDragCurrent] = useState<{ x: number; y: number } | null>(null);
  const [cropSelection, setCropSelection] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  // Optional size the cropped picture is scaled to afterwards ("crop to 1:1 and make it 600 x 600").
  const [outWidth, setOutWidth] = useState("");
  // Which kind of drag is currently happening -- "create" is the
  // original drag-out-a-new-box behavior; "move" repositions the whole
  // existing selection without resizing it; the 4 corner values resize
  // from that corner while keeping the OPPOSITE corner fixed in place
  // (the standard, expected behavior of every crop tool's corner
  // handles). Distinguishing these is what makes an existing selection
  // genuinely adjustable instead of only ever re-drawable from scratch.
  const [interactionMode, setInteractionMode] = useState<"create" | "move" | "nw" | "ne" | "sw" | "se" | null>(null);
  // Captured once at the start of a move/resize gesture: the selection
  // as it was before this drag began, plus the exact point the drag
  // started at -- both are needed to compute a move/resize purely as an
  // offset from the start, rather than accumulating small per-frame
  // errors across many move events.
  const dragOriginRef = useRef<{ selection: { x: number; y: number; w: number; h: number }; point: { x: number; y: number } } | null>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const srcCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const resizePreviewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setFile(f); setResult(null); setCropResultUrl(null);
    setRotate("0"); setFlip(false); setFlop(false); setGrayscale(false);
    setOrigDims({ w: 0, h: 0 }); setWidth(""); setHeight("");
    setCropSelection(null);
    setResizePreviewUrl(null);
    // decodeImage also opens iPhone (HEIC), AVIF and TIFF photos.
    decodeImage(f).then(async ({ canvas }) => {
      srcCanvasRef.current = canvas;
      setPreviewUrl(URL.createObjectURL(await canvasToBlob(canvas, "image/png")));
      setOrigDims({ w: canvas.width, h: canvas.height });
      setWidth(String(canvas.width)); setHeight(String(canvas.height));
    }).catch((e) => { toast.error(e?.message || "This file could not be opened as an image"); setFile(null); });
  }, []);

  // Turning is applied first, so the size typed in is the size of the turned picture.
  const turned = turnedSize(origDims.w, origDims.h, rotate);

  const changeWidth = (value: string) => {
    setWidth(value);
    setResult(null); setCropResultUrl(null);
    if (maintainRatio && turned.w > 0) {
      const w = Number(value);
      if (w > 0) setHeight(String(Math.round(w * (turned.h / turned.w))));
    }
  };

  const changeHeight = (value: string) => {
    setHeight(value);
    setResult(null); setCropResultUrl(null);
    if (maintainRatio && turned.h > 0) {
      const h = Number(value);
      if (h > 0) setWidth(String(Math.round(h * (turned.w / turned.h))));
    }
  };

  const changeRotate = (value: string) => {
    const swaps = turnedSize(1, 2, value).w !== turnedSize(1, 2, rotate).w;
    setRotate(value);
    if (swaps) { setWidth(height); setHeight(width); }
    setResult(null); setCropResultUrl(null);
  };

  /** The whole resize pipeline, shared by the preview and the real result: turn, fit to the size, black and white. */
  const render = (src: HTMLCanvasElement, w: number, h: number, mime: string): HTMLCanvasElement => {
    let c = rotateFlip(src, Number(rotate) as 0 | 90 | 180 | 270, flip, flop);
    const bg = usePad ? parseColor(padColor) ?? undefined : undefined;
    c = fitCanvas(c, w, h, maintainRatio ? "fill" : fit, noEnlarge, bg && bg.a > 0 ? bg : undefined);
    if (grayscale) { if (c === src) { const copy = document.createElement("canvas"); copy.width = c.width; copy.height = c.height; copy.getContext("2d")!.drawImage(c, 0, 0); c = copy; } toGrayscale(c); }
    // JPEG cannot be see-through: padding would come out black.
    return mime === "image/jpeg" ? flattenOnColor(c, "#ffffff") : c;
  };

  // Regenerates the "what this will actually look like" preview a short
  // moment after width/height stop changing, rather than on every
  // keystroke -- cheap, but no reason to redraw a canvas dozens of times
  // while someone's still typing a number.
  useEffect(() => {
    if (!file || mode !== "resize") return;
    if (resizePreviewTimer.current) clearTimeout(resizePreviewTimer.current);
    resizePreviewTimer.current = setTimeout(async () => {
      const w = Number(width), h = Number(height);
      if (!w || !h || w <= 0 || h <= 0) { setResizePreviewUrl(null); return; }
      const srcC = srcCanvasRef.current;
      if (!srcC || w * h > 4_000_000) { setResizePreviewUrl(null); return; }
      const canvas = render(srcC, w, h, "image/png");
      canvas.toBlob((blob) => { if (blob) setResizePreviewUrl(URL.createObjectURL(blob)); });
    }, 250);
    return () => { if (resizePreviewTimer.current) clearTimeout(resizePreviewTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, mode, width, height, fit, noEnlarge, padColor, usePad, rotate, flip, flop, grayscale, maintainRatio]);

  const resize = async () => {
    if (!file) return;
    setProcessing(true);
    try {
      const w = Math.round(Number(width));
      const h = Math.round(Number(height));
      if (!(w >= 1) || !(h >= 1)) { toast.error("Width and height must each be at least 1 px"); setProcessing(false); return; }
      if (w > MAX_SIDE || h > MAX_SIDE || w * h > MAX_PIXELS) { toast.error("That size is too large for the browser to make"); setProcessing(false); return; }
      if (w * h > 16_000_000) toast.warning("This is very large (over 16 megapixels) and may fail on phones");
      const { canvas: src } = await decodeImage(file);
      const { blob } = await canvasToBlobMatchingFormat(render(src, w, h, getImageOutputFormat(file).mimeType), file);
      setResult(blob);
      toast.success(`Resized to ${w}x${h}`);
    } catch { toast.error("Failed to resize"); }
    finally { setProcessing(false); }
  };

  const applyCrop = async () => {
    if (!file || !cropSelection) return;
    setProcessing(true);
    try {
      let { blob, width: sw, height: sh } = await cropImage(file, cropSelection);
      const ow = Math.round(Number(outWidth));
      if (ow >= 1 && ow !== sw) {
        const oh = Math.max(1, Math.round(sh * (ow / sw)));
        const scaled = await resizeImage(new File([blob], file.name, { type: blob.type }), ow, oh);
        blob = scaled.blob; sw = ow; sh = oh;
      }
      setResult(blob);
      // The real, confirmed gap this fixes: previously nothing visually
      // showed what the crop actually produced -- the selection overlay
      // on the ORIGINAL full image stayed on screen unchanged, so
      // there was no way to tell the crop genuinely worked short of
      // downloading the file and opening it separately. A real preview
      // of the actual cropped result, built from the same blob that
      // gets downloaded (not a re-derived approximation), is what
      // makes the crop visually confirmed.
      setCropResultUrl(URL.createObjectURL(blob));
      toast.success(`Cropped to ${sw}x${sh}`);
    } catch { toast.error("Failed to crop"); }
    finally { setProcessing(false); }
  };

  const fracFromPoint = (clientX: number, clientY: number) => {
    const rect = imgWrapRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    };
  };
  // Kept as a thin wrapper (rather than replacing all mouse-event call
  // sites) so the existing mouse handlers below don't need to change at
  // all -- fracFromPoint is the one real implementation both mouse and
  // touch paths now share.
  const fracFromEvent = (e: React.MouseEvent<HTMLDivElement>) => fracFromPoint(e.clientX, e.clientY);

  // Shared, event-agnostic core -- both the mouse handlers and the new
  // touch handlers below call these same two functions with a plain
  // {x,y} point, rather than duplicating this logic (especially the
  // aspect-ratio-constraint math) a second time for touch.
  // Computes a resize from a fixed opposite corner given the current
  // drag point -- shared by both "create" (fixed corner = where the
  // drag started) and the 4 named corner-resize modes (fixed corner =
  // the selection's own opposite corner, captured in dragOriginRef when
  // the resize began), since both are really the same operation: one
  // corner moves, the other stays put, optionally constrained to a
  // fixed aspect ratio along the way.
  const boxFromFixedCorner = (fixed: { x: number; y: number }, raw: { x: number; y: number }) => {
    if (aspectRatio === null || !origDims.w || !origDims.h) {
      return { x: Math.min(fixed.x, raw.x), y: Math.min(fixed.y, raw.y), w: Math.abs(raw.x - fixed.x), h: Math.abs(raw.y - fixed.y) };
    }
    // Fractional (0-1) coordinates are relative to the displayed box's
    // own width/height separately -- since that box has the same aspect
    // ratio as the real image (the preview never stretches it), a
    // fractional width and a fractional height of the same NUMBER are
    // only a real square in pixels if the source image itself happens
    // to be square. Converting the desired pixel aspect ratio into the
    // matching fractional ratio (scaled by the image's real width/height)
    // is what makes a "1:1" selection actually crop out a real square,
    // not just a square-looking fraction of a non-square image.
    const fractionalRatio = aspectRatio * (origDims.h / origDims.w);
    const signX = raw.x >= fixed.x ? 1 : -1;
    const signY = raw.y >= fixed.y ? 1 : -1;
    const rawW = Math.abs(raw.x - fixed.x);
    const rawH = Math.abs(raw.y - fixed.y);
    // Constrains by whichever axis moved further (as a fraction of that
    // axis's own available room), so the selection grows in the
    // direction actually being dragged rather than always pinned to one
    // axis -- then clamps to the image's own edges so the selection can
    // never ask for a corner outside the actual picture.
    let w = rawW, h = rawW / fractionalRatio;
    if (rawH * fractionalRatio > rawW) { h = rawH; w = rawH * fractionalRatio; }
    const maxW = signX > 0 ? 1 - fixed.x : fixed.x;
    const maxH = signY > 0 ? 1 - fixed.y : fixed.y;
    if (w > maxW) { w = maxW; h = w / fractionalRatio; }
    if (h > maxH) { h = maxH; w = h * fractionalRatio; }
    const cornerX = fixed.x + signX * w;
    const cornerY = fixed.y + signY * h;
    return { x: Math.min(fixed.x, cornerX), y: Math.min(fixed.y, cornerY), w, h };
  };

  const startDragAt = (point: { x: number; y: number }) => {
    if (mode !== "crop") return;
    setInteractionMode("create");
    setDragStart(point);
    setDragCurrent(point);
  };
  // Begins adjusting the EXISTING selection instead of always starting
  // a brand new one from scratch -- this is the real fix for "no
  // corner handles to grab, can't adjust the selection," not just the
  // touch-event bug. "move" repositions the whole box; the 4 corner
  // values resize from that corner while the opposite corner stays
  // fixed, the standard behavior every crop tool's handles have.
  const startAdjust = (adjustMode: "move" | "nw" | "ne" | "sw" | "se", point: { x: number; y: number }) => {
    if (!cropSelection) return;
    setInteractionMode(adjustMode);
    dragOriginRef.current = { selection: cropSelection, point };
  };
  const moveDragTo = (raw: { x: number; y: number }) => {
    if (mode !== "crop") return;
    if (interactionMode === "create") {
      if (!dragStart) return;
      // "create" mode's own box is derived elsewhere from
      // dragStart+dragCurrent (the moving corner), so only the moving
      // corner itself needs to be stored here -- boxFromFixedCorner
      // returns a full box, and this extracts just that one corner's
      // point from it, matching the aspect-ratio-constrained shape a
      // free drag would have produced.
      const box = boxFromFixedCorner(dragStart, raw);
      setDragCurrent({ x: box.x === dragStart.x ? box.x + box.w : box.x, y: box.y === dragStart.y ? box.y + box.h : box.y });
      return;
    }
    const origin = dragOriginRef.current;
    if (!origin) return;
    if (interactionMode === "move") {
      const dx = raw.x - origin.point.x;
      const dy = raw.y - origin.point.y;
      const s = origin.selection;
      // Clamped so the box can be dragged right up to each edge but
      // never pushed partially outside the image.
      const x = Math.max(0, Math.min(1 - s.w, s.x + dx));
      const y = Math.max(0, Math.min(1 - s.h, s.y + dy));
      setCropSelection({ x, y, w: s.w, h: s.h });
      setResult(null); setCropResultUrl(null);
      return;
    }
    if (interactionMode === "nw" || interactionMode === "ne" || interactionMode === "sw" || interactionMode === "se") {
      const s = origin.selection;
      // The corner OPPOSITE the one being dragged is what stays fixed
      // -- e.g. dragging the nw (top-left) handle keeps the se
      // (bottom-right) corner anchored in place, which is what lets
      // someone precisely pull just one corner of an existing
      // selection instead of only ever re-drawing the whole thing.
      const fixed = {
        x: interactionMode.includes("w") ? s.x + s.w : s.x,
        y: interactionMode.includes("n") ? s.y + s.h : s.y,
      };
      setCropSelection(boxFromFixedCorner(fixed, raw));
      setResult(null); setCropResultUrl(null);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => startDragAt(fracFromEvent(e));
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => moveDragTo(fracFromEvent(e));

  // Real touch support -- this was the actual, confirmed bug: the crop
  // area only ever had onMouseDown/onMouseMove/onMouseUp, which touch
  // interactions on a phone or tablet don't reliably fire the same way
  // a mouse does (a tap can synthesize a single click-like event pair,
  // but a drag/swipe gesture needs the browser's separate
  // touchstart/touchmove/touchend events, which nothing here was
  // listening for at all) -- so dragging to select a crop region simply
  // never worked on a touch device, exactly matching "nothing happens,
  // no corner handles to grab" reported directly. e.preventDefault() on
  // touchmove specifically stops the whole page from scrolling while
  // someone is trying to drag out a selection, which is the other half
  // of what makes this usable on a touch screen, not just theoretically
  // wired up.
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (mode !== "crop" || e.touches.length === 0) return;
    const t = e.touches[0];
    startDragAt(fracFromPoint(t.clientX, t.clientY));
  };
  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (mode !== "crop" || !interactionMode || e.touches.length === 0) return;
    e.preventDefault();
    const t = e.touches[0];
    moveDragTo(fracFromPoint(t.clientX, t.clientY));
  };

  const finishDrag = () => {
    if (mode !== "crop") { setDragStart(null); setDragCurrent(null); setInteractionMode(null); dragOriginRef.current = null; return; }
    if (interactionMode === "create" && dragStart && dragCurrent) {
      const w = Math.abs(dragCurrent.x - dragStart.x);
      const h = Math.abs(dragCurrent.y - dragStart.y);
      if (w >= MIN_CROP_FRAC && h >= MIN_CROP_FRAC) {
        setCropSelection({ x: Math.min(dragStart.x, dragCurrent.x), y: Math.min(dragStart.y, dragCurrent.y), w, h });
        setResult(null); setCropResultUrl(null);
      }
    }
    setDragStart(null);
    setDragCurrent(null);
    setInteractionMode(null);
    dragOriginRef.current = null;
  };

  const activeSelection = dragStart && dragCurrent
    ? { x: Math.min(dragStart.x, dragCurrent.x), y: Math.min(dragStart.y, dragCurrent.y), w: Math.abs(dragCurrent.x - dragStart.x), h: Math.abs(dragCurrent.y - dragStart.y) }
    : cropSelection;

  // The extension follows the bytes actually written (a GIF comes out as PNG, so it is named .png).
  const outExtension = (() => {
    const t = result?.type;
    if (t === "image/jpeg") return "jpg";
    if (t === "image/png" || t === "image/webp" || t === "image/gif" || t === "image/bmp") return t.split("/")[1];
    return file ? getImageOutputFormat(file).extension : "png";
  })();
  const downloadName = () => withCorrectExtension(`${mode === "crop" ? "cropped" : "resized"}_${file?.name || "image"}`, outExtension);
  const loadingImage = !!file && origDims.w === 0;
  const widthError = mode === "resize" && !loadingImage ? sizeProblem("width", width) : null;
  const heightError = mode === "resize" && !loadingImage ? sizeProblem("height", height) : null;
  const areaError = !widthError && !heightError && mode === "resize" && !loadingImage && Number(width) * Number(height) > MAX_PIXELS
    ? `That is ${Math.round((Number(width) * Number(height)) / 1_000_000)} megapixels; the limit is 100 megapixels` : null;
  const sizeInvalid = !!(widthError || heightError || areaError);
  const isAnimatedType = file?.type === "image/gif";

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="image/*" onFiles={handleFiles} label="Drop an image to resize or crop" description="Resize to exact dimensions, or crop to a selected area" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card">
            <p className="text-sm font-medium">{file.name}</p>
            <p className="text-xs text-muted-foreground">Original: {origDims.w} x {origDims.h}px</p>
          </div>

          <div className="flex gap-2">
            <Button size="sm" variant={mode === "resize" ? "default" : "outline"} onClick={() => { setMode("resize"); setResult(null); setCropResultUrl(null); }} className="gap-1.5">
              <Maximize2 className="h-3.5 w-3.5" /> Resize
            </Button>
            <Button size="sm" variant={mode === "crop" ? "default" : "outline"} onClick={() => { setMode("crop"); setResult(null); setCropResultUrl(null); }} className="gap-1.5">
              <Crop className="h-3.5 w-3.5" /> Crop
            </Button>
          </div>

          {mode === "resize" ? (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer w-fit">
                <input type="checkbox" checked={maintainRatio} onChange={(e) => { setMaintainRatio(e.target.checked); setResult(null); }} />
                Maintain aspect ratio
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div><Label htmlFor="rc-width">Width (px)</Label><Input id="rc-width" type="number" min={1} max={MAX_SIDE} step={1} inputMode="numeric" disabled={loadingImage} aria-invalid={widthError ? true : undefined} value={width} onChange={e => changeWidth(e.target.value)} />{widthError && <p role="alert" className="text-xs text-destructive mt-1">{widthError}</p>}</div>
                <div><Label htmlFor="rc-height">Height (px)</Label><Input id="rc-height" type="number" min={1} max={MAX_SIDE} step={1} inputMode="numeric" disabled={loadingImage} aria-invalid={heightError ? true : undefined} value={height} onChange={e => changeHeight(e.target.value)} />{heightError && <p role="alert" className="text-xs text-destructive mt-1">{heightError}</p>}</div>
              </div>
              {areaError && <p role="alert" className="text-xs text-destructive">{areaError}</p>}
              {!sizeInvalid && turned.w > 0 && Number(width) > turned.w * 2 && <p className="text-xs text-muted-foreground">Enlarging an image more than twice its size makes it look soft or blurry.</p>}
              {isAnimatedType && <p className="text-xs text-muted-foreground">Animated GIFs come out as a still PNG (first frame); the animation is not kept.</p>}
              <div className="flex flex-wrap items-end gap-2 text-sm">
                <div>
                  <Label>Or set the width in</Label>
                  <select aria-label="Unit" value={unit} onChange={e => setUnit(e.target.value as "%" | "cm" | "in")} className="border rounded px-2 py-2 bg-background block">
                    <option value="%">percent of original</option>
                    <option value="cm">centimetres (print)</option>
                    <option value="in">inches (print)</option>
                  </select>
                </div>
                <div><Label>{unit === "%" ? "Percent" : unit === "cm" ? "Width (cm)" : "Width (in)"}</Label><Input className="w-24" type="number" min={0} value={unitValue} onChange={e => setUnitValue(e.target.value)} /></div>
                {unit !== "%" && <div><Label>DPI</Label><Input aria-label="DPI" className="w-20" type="number" min={1} value={dpi} onChange={e => setDpi(e.target.value)} /></div>}
                <Button type="button" variant="outline" size="sm" onClick={() => {
                  const v = Number(unitValue);
                  if (!(v > 0) || !origDims.w) { toast.error("Enter a number above 0"); return; }
                  const px = unit === "%" ? origDims.w * v / 100 : v * (unit === "cm" ? 1 / 2.54 : 1) * (Number(dpi) || 300);
                  changeWidth(String(Math.max(1, Math.round(px))));
                }}>Set width</Button>
              </div>
              {!maintainRatio && (
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label className="text-xs">How the picture fits the width and height</Label>
                    <Select value={fit} onValueChange={(v) => { setFit(v as FitMode); setResult(null); }}>
                      <SelectTrigger className="w-40" aria-label="How the picture fits the size"><SelectValue /></SelectTrigger>
                      <SelectContent>{FIT_MODES.map((m) => <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-muted-foreground">{FIT_HELP[fit]} With Inside or Outside the result may not be exactly the width and height you typed.</p>
                  {fit === "contain" && (
                    <div className="space-y-2">
                      <Check id="rc-usepad" checked={usePad} onChange={(v) => { setUsePad(v); setResult(null); }} label="Fill the leftover space with a color" help="Off leaves it see-through (white for JPEG)." />
                      {usePad && <ColorPicker id="rc-pad" form={{ bgColor: padColor }} onChange={(p) => { setPadColor(p.bgColor); setResult(null); }} />}
                    </div>
                  )}
                </div>
              )}
              <Check id="rc-noenlarge" checked={noEnlarge} onChange={(v) => { setNoEnlarge(v); setResult(null); }} label="Never make it bigger than the original" />
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Turn, mirror and color (optional)</legend>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Rotate clockwise</Label>
                    <Select value={rotate} onValueChange={changeRotate}>
                      <SelectTrigger className="w-32" aria-label="Rotate"><SelectValue /></SelectTrigger>
                      <SelectContent>{["0", "90", "180", "270"].map((d) => <SelectItem key={d} value={d}>{d === "0" ? "Not at all" : `${d} degrees`}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Check id="rc-flop" checked={flop} onChange={(v) => { setFlop(v); setResult(null); }} label="Mirror left to right" />
                  <Check id="rc-flip" checked={flip} onChange={(v) => { setFlip(v); setResult(null); }} label="Mirror top to bottom" />
                  <Check id="rc-gray" checked={grayscale} onChange={(v) => { setGrayscale(v); setResult(null); }} label="Black and white" />
                </div>
                {rotate !== "0" && <p className="text-xs text-muted-foreground">The picture is turned first, so the width and height above are for the turned picture.</p>}
              </fieldset>
              {resizePreviewUrl && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Preview at {width} x {height}px</p>
                  <div className="border rounded-lg overflow-hidden bg-muted/20 mx-auto" style={{ maxWidth: "400px" }}>
                    <img src={resizePreviewUrl} alt="Resize preview" className="w-full h-auto block" />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2 flex-wrap">
                {ASPECT_PRESETS.map(p => (
                  <Button
                    key={p.label}
                    size="sm"
                    variant={aspectRatio === p.ratio ? "default" : "outline"}
                    onClick={() => {
                      setAspectRatio(p.ratio); setResult(null); setCropResultUrl(null);
                      if (p.ratio === null || origDims.w === 0) { setCropSelection(null); return; }
                      // Start from the largest centred box with this shape; it can then be dragged or typed over.
                      const W = origDims.w, H = origDims.h;
                      const pw = W / H > p.ratio ? p.ratio * H : W;
                      const ph = W / H > p.ratio ? H : W / p.ratio;
                      setCropSelection({ x: (W - pw) / 2 / W, y: (H - ph) / 2 / H, w: pw / W, h: ph / H });
                    }}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              {origDims.w > 0 && (() => {
                const px = cropSelection
                  ? { x: Math.round(cropSelection.x * origDims.w), y: Math.round(cropSelection.y * origDims.h), w: Math.round((cropSelection.x + cropSelection.w) * origDims.w) - Math.round(cropSelection.x * origDims.w), h: Math.round((cropSelection.y + cropSelection.h) * origDims.h) - Math.round(cropSelection.y * origDims.h) }
                  : { x: 0, y: 0, w: origDims.w, h: origDims.h };
                // Typed numbers (in pixels of the original) move the box; it is kept inside the picture.
                const set = (k: "x" | "y" | "w" | "h", v: string) => {
                  const n = Math.round(Number(v));
                  if (!Number.isFinite(n)) return;
                  const next = { ...px, [k]: n };
                  next.w = Math.max(1, Math.min(next.w, origDims.w)); next.h = Math.max(1, Math.min(next.h, origDims.h));
                  next.x = Math.max(0, Math.min(next.x, origDims.w - next.w)); next.y = Math.max(0, Math.min(next.y, origDims.h - next.h));
                  setCropSelection({ x: next.x / origDims.w, y: next.y / origDims.h, w: next.w / origDims.w, h: next.h / origDims.h });
                  setResult(null); setCropResultUrl(null);
                };
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm items-end">
                    {(["x", "y", "w", "h"] as const).map((k) => (
                      <label key={k} className="text-xs">{{ x: "X (px from left)", y: "Y (px from top)", w: "Width (px)", h: "Height (px)" }[k]}
                        <input type="number" min={0} className="border rounded px-2 py-1 w-full bg-background text-sm" value={px[k]} onChange={(e) => set(k, e.target.value)} />
                      </label>
                    ))}
                    <label className="text-xs">Then scale to width (px, optional)
                      <input type="number" min={1} placeholder="same" className="border rounded px-2 py-1 w-full bg-background text-sm" value={outWidth} onChange={(e) => { setOutWidth(e.target.value); setResult(null); setCropResultUrl(null); }} />
                    </label>
                  </div>
                );
              })()}
              {previewUrl && (
                <div
                  ref={imgWrapRef}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={finishDrag}
                  onMouseLeave={finishDrag}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={finishDrag}
                  onTouchCancel={finishDrag}
                  className="relative border rounded-lg overflow-hidden bg-white mx-auto"
                  style={{ maxWidth: "500px", cursor: "crosshair", touchAction: "none" }}
                >
                  <img src={previewUrl} alt="Preview" className="w-full h-auto block pointer-events-none select-none" />
                  {activeSelection && (
                    <div
                      onMouseDown={e => { if (cropSelection) { e.stopPropagation(); startAdjust("move", fracFromEvent(e)); } }}
                      onTouchStart={e => {
                        if (cropSelection && e.touches.length > 0) {
                          e.stopPropagation();
                          const t = e.touches[0];
                          startAdjust("move", fracFromPoint(t.clientX, t.clientY));
                        }
                      }}
                      className={`absolute border-2 border-primary bg-primary/20 ${cropSelection ? "cursor-move" : "pointer-events-none"}`}
                      style={{
                        left: `${activeSelection.x * 100}%`,
                        top: `${activeSelection.y * 100}%`,
                        width: `${activeSelection.w * 100}%`,
                        height: `${activeSelection.h * 100}%`,
                        touchAction: "none",
                      }}
                    >
                      {/* Real, draggable corner handles -- only shown once a
                          selection is actually committed (not during the
                          initial drag-to-create gesture), since adjusting an
                          existing selection precisely is a genuinely
                          different, real gap this fixes: previously the
                          only way to change a selection at all was
                          re-dragging a brand new one from scratch, with
                          nothing to grab on the existing box. Each handle
                          resizes from that corner while the OPPOSITE corner
                          stays anchored, the standard behavior every crop
                          tool's handles have. Sized generously (larger than
                          their visible dot) specifically for touch targets,
                          which need to be bigger than a mouse cursor needs. */}
                      {cropSelection && (["nw", "ne", "sw", "se"] as const).map(corner => (
                        <div
                          key={corner}
                          onMouseDown={e => { e.stopPropagation(); startAdjust(corner, fracFromEvent(e)); }}
                          onTouchStart={e => {
                            e.stopPropagation();
                            if (e.touches.length === 0) return;
                            const t = e.touches[0];
                            startAdjust(corner, fracFromPoint(t.clientX, t.clientY));
                          }}
                          className="absolute h-6 w-6 -m-3 flex items-center justify-center"
                          style={{
                            top: corner.includes("n") ? 0 : "100%",
                            left: corner.includes("w") ? 0 : "100%",
                            cursor: corner === "nw" || corner === "se" ? "nwse-resize" : "nesw-resize",
                            touchAction: "none",
                          }}
                        >
                          <div className="h-3.5 w-3.5 rounded-full bg-primary border-2 border-white shadow" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {cropSelection ? "Drag a corner to resize, drag inside the box to move it, or draw a new selection elsewhere." : "Drag on the image to select the area to keep."}
              </p>
            </div>
          )}

          {mode === "crop" && cropResultUrl && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Cropped result — this is what you'll get</p>
              <div className="border rounded-lg overflow-hidden bg-muted/20 mx-auto" style={{ maxWidth: "400px" }}>
                <img src={cropResultUrl} alt="Cropped result preview" className="w-full h-auto block" />
              </div>
            </div>
          )}

          <div className="flex gap-3">
            {processing ? <ProcessingState /> : result ? (
              <>
                <DownloadButton blob={result} fileName={downloadName()} />
                {mode === "crop" && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Hands the just-cropped result off directly into
                      // Resize mode as the new working file, rather than
                      // forcing a download-then-re-upload round trip to
                      // do both in sequence -- the same "carry the result
                      // forward" pattern already used for Organize PDF's
                      // deep-link from Merge.
                      const croppedFile = new File([result], file!.name, { type: result.type });
                      setFile(croppedFile);
                      setPreviewUrl(URL.createObjectURL(result));
                      setResizePreviewUrl(null);
                      setResult(null); setCropResultUrl(null);
                      setCropSelection(null);
                      setRotate("0"); setFlip(false); setFlop(false);
                      setMode("resize");
                      const img = new Image();
                      img.onload = () => { setOrigDims({ w: img.naturalWidth, h: img.naturalHeight }); setWidth(String(img.naturalWidth)); setHeight(String(img.naturalHeight)); };
                      img.src = URL.createObjectURL(result);
                    }}
                  >
                    Resize this crop
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setFile(null); setResult(null); setCropResultUrl(null); setCropSelection(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={mode === "resize" ? resize : applyCrop} disabled={(mode === "crop" && !cropSelection) || (mode === "resize" && (loadingImage || sizeInvalid))}>
                  {mode === "resize" ? "Resize Image" : "Apply Crop"}
                </Button>
                <Button variant="outline" onClick={() => setFile(null)}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
